<template>
    <Teleport to="body">
        <transition name="fade">
            <div
                v-if="modelValue"
                class="fixed inset-0 z-50 flex items-center justify-center"
                role="dialog"
                aria-labelledby="community-modal-title"
            >
                <!-- Overlay -->
                <div
                    class="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    @click="close"
                ></div>

                <!-- Desktop Modal -->
                <div
                    class="relative bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden hidden md:block"
                >
                    <!-- Close button -->
                    <button
                        type="button"
                        class="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center rounded-full transition-colors"
                        @click="close"
                    >
                        <Icon
                            icon="x"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200"
                        />
                    </button>

                    <!-- Preview image -->
                    <div class="relative bg-slate-100 dark:bg-slate-700">
                        <img
                            src="/images/community-preview.png"
                            alt="Community preview"
                            class="w-full"
                        />
                    </div>

                    <!-- Content -->
                    <div class="p-6 bg-white dark:bg-slate-800">
                        <h2
                            id="community-modal-title"
                            class="text-xl font-medium text-slate-800 dark:text-slate-100"
                        >
                            Members-only travel community
                        </h2>
                        <p class="text-sm text-slate-600 dark:text-slate-300 mt-2">
                            A members-only forum where travelers share real-life
                            tips on trips, credit cards, and saving money.
                        </p>

                        <ul class="mt-5 space-y-2">
                            <li
                                v-for="feature in features"
                                :key="feature"
                                class="flex items-center gap-3"
                            >
                                <svg
                                    class="w-5 h-5 text-10x-orange shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                <span class="text-sm text-slate-700 dark:text-slate-200">
                                    {{ feature }}
                                </span>
                            </li>
                        </ul>

                        <!-- Footer buttons -->
                        <div class="flex items-center justify-end gap-3 mt-6">
                            <button
                                type="button"
                                class="px-4 py-2.5 text-sm font-medium text-slate-700 dark:text-slate-200 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-900/40 transition-colors"
                                @click="close"
                            >
                                Close
                            </button>
                            <a
                                :href="exploreUrl"
                                target="_blank"
                                class="px-4 py-2.5 text-sm font-medium text-white bg-10x-orange rounded shadow-sm hover:bg-orange-600 transition-colors"
                            >
                                Explore community
                            </a>
                        </div>
                    </div>
                </div>

                <!-- Mobile Modal (full screen) -->
                <div
                    class="fixed inset-0 bg-white dark:bg-slate-800 flex flex-col md:hidden overflow-y-auto"
                >
                    <!-- Close button -->
                    <button
                        type="button"
                        class="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center"
                        @click="close"
                    >
                        <Icon
                            icon="x"
                            class="w-5 h-5 text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:text-slate-200"
                        />
                    </button>

                    <!-- Preview image -->
                    <div class="relative bg-slate-100 dark:bg-slate-700 shrink-0">
                        <img
                            src="/images/community-preview-mobile.png"
                            alt="Community preview"
                            class="w-full"
                        />
                    </div>

                    <!-- Content -->
                    <div class="flex-1 px-6 pb-6 bg-white dark:bg-slate-800">
                        <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100">
                            Members-only travel community
                        </h2>
                        <p class="text-sm text-slate-600 dark:text-slate-300 mt-2">
                            A members-only forum where travelers share real-life
                            tips on trips, credit cards, and saving money.
                        </p>

                        <ul class="mt-5 space-y-2">
                            <li
                                v-for="feature in features"
                                :key="feature"
                                class="flex items-center gap-3"
                            >
                                <svg
                                    class="w-5 h-5 text-10x-orange shrink-0"
                                    viewBox="0 0 20 20"
                                    fill="currentColor"
                                >
                                    <path
                                        fill-rule="evenodd"
                                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                        clip-rule="evenodd"
                                    />
                                </svg>
                                <span class="text-sm text-slate-700 dark:text-slate-200">
                                    {{ feature }}
                                </span>
                            </li>
                        </ul>
                    </div>

                    <!-- Mobile footer -->
                    <div class="px-6 pb-8 pt-4 shrink-0">
                        <a
                            :href="exploreUrl"
                            target="_blank"
                            class="block w-full py-3 text-sm font-medium text-center text-white bg-10x-orange rounded-lg shadow-sm hover:bg-orange-600 transition-colors"
                        >
                            Explore Community
                        </a>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import Icon from '@/components/ui/icon/icon.vue';

const features = [
    'Proven travel and card tips and tricks',
    'Smarter ways to save and earn rewards',
    'Insights from experienced members',
];

defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    exploreUrl: {
        type: String,
        default: '',
    },
});

const emit = defineEmits(['update:modelValue']);

const close = () => {
    emit('update:modelValue', false);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
